import { api } from "./api";
import { TokenManager } from "./auth/tokenManager";
import { sendToTerminal } from "./terminalLogger";
import { API_URL } from "../config";
import { refreshToken } from "./auth";
import { getTenantId } from "../utils/getTenantId";
import { TokenService } from "./tokenService";

// ========================
// CONSTANTES DE CONFIGURAÇÃO
// ========================

/**
 * Duração do SSO Token em segundos (conforme backend: 8 horas)
 * Ver: SysOne1Tech-CorelDraw-Backend/src/util/auth.ts → generateJWT_SSO() → exp = iat + 28800
 */
const SSO_TOKEN_DURATION_SEC = 28800;

/**
 * Threshold para renovação proativa (renovar 5 minutos antes de expirar)
 * Reduzido de 30s para 300s para evitar renovações desnecessárias
 */
const RENEWAL_THRESHOLD_SEC = 300;

/**
 * Cooldown entre renovações (evita renovações muito frequentes)
 */
const REFRESH_COOLDOWN_MS = 5000;

/**
 * Timeout para requisições de refresh SSO (10 segundos)
 */
const SSO_REFRESH_TIMEOUT_MS = 10000;

// ========================
// CONTROLE DE RENOVAÇÃO
// ========================

/**
 * Lock para evitar renovações simultâneas
 * Quando uma renovação está em andamento, outras requisições aguardam ela
 */
let refreshInProgress: Promise<{ token: string; expiresAt?: string }> | null =
	null;

/**
 * Timestamp da última renovação (para cooldown)
 */
let lastRefreshTime = 0;

let ssoAutoTimer: number | null = null;

export function setShortSsoToken(token: string, expiresAtIso?: string) {
	TokenManager.setSsoToken(token, expiresAtIso);
	// Agendar auto-refresh proativo
	try {
		scheduleSsoAutoRefresh();
	} catch {}
}

export function getShortSsoToken(): string | null {
	return TokenManager.getSsoToken();
}

export function getExpiresAtIso(): string | null {
	const exp = TokenManager.getSsoExpiration();
	if (exp) return exp;
	return null;
}

export function secondsToExpiry(): number | null {
	const iso = getExpiresAtIso();
	if (iso) {
		const diffMs = new Date(iso).getTime() - Date.now();
		return Math.floor(diffMs / 1000);
	}

	// Fallback: se não há expiresAt salvo, tentar decodificar do token
	const ssoToken = getShortSsoToken();
	if (ssoToken) {
		const expSec = decodeExp(ssoToken);
		if (expSec != null) {
			const diffMs = expSec * 1000 - Date.now();
			return Math.floor(diffMs / 1000);
		}
	}
	return null;
}

/**
 * Agenda um auto-refresh proativo do SSO para ocorrer antes do vencimento.
 * thresholdSeconds define com quantos segundos restantes o refresh será disparado (padrão 300s = 5 minutos).
 *
 * ✅ OTIMIZADO: Threshold aumentado de 120s para 300s (5 minutos antes de expirar)
 * para reduzir renovações desnecessárias, considerando que o token tem 1 hora de duração.
 */
export function scheduleSsoAutoRefresh(
	thresholdSeconds: number = RENEWAL_THRESHOLD_SEC,
) {
	try {
		if (ssoAutoTimer != null) {
			window.clearTimeout(ssoAutoTimer);
			ssoAutoTimer = null;
		}
	} catch {}

	const secs = secondsToExpiry();
	if (secs == null) return; // sem exp

	// ✅ NOVO: Não agendar se já está sendo renovado
	if (refreshInProgress) {
		refreshInProgress
			.then(() => {
				scheduleSsoAutoRefresh(thresholdSeconds);
			})
			.catch(() => {
				scheduleSsoAutoRefresh(thresholdSeconds);
			});
		return;
	}

	const delayMs = Math.max((secs - thresholdSeconds) * 1000, 0);

	// ✅ NOVO: Não agendar se delay for muito curto (< 10s)
	if (delayMs < 10000) {
		return;
	}

	ssoAutoTimer = window.setTimeout(async () => {
		try {
			// Tentar refresh próprio primeiro
			try {
				await refreshSsoToken();
			} catch (e) {
				// Fallback: emissão com base bearer, se disponível
				try {
					await issueSsoTokenFallback();
				} catch (e2) {
					// Silencioso - erro já será logado em issueSsoTokenFallback se crítico
				}
			}
		} finally {
			// Reagendar para o próximo ciclo
			try {
				scheduleSsoAutoRefresh(thresholdSeconds);
			} catch {}
		}
	}, delayMs);
}

export function stopSsoAutoRefresh() {
	try {
		if (ssoAutoTimer != null) {
			window.clearTimeout(ssoAutoTimer);
			ssoAutoTimer = null;
		}
	} catch {}
}

function decodeExp(token: string): number | null {
	try {
		const [, payload] = token.split(".") as [string, string, string];
		const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		const obj = JSON.parse(json);
		return typeof obj?.exp === "number" ? obj.exp : null;
	} catch {
		return null;
	}
}

/**
 * Renovar token SSO usando o próprio token curto (independente do Docker 1)
 * Inclui timeout de 10 segundos para evitar requisições travadas
 */
export async function refreshSsoToken(): Promise<{
	token: string;
	expiresAt?: string;
}> {
	const currentSso = getShortSsoToken();
	if (!currentSso) throw new Error("SSO refresh: token ausente");
	const endpoint = `${API_URL}/auth/cdr/sso-token/refresh`;
	const tenantId = getTenantId();

	// Criar AbortController para timeout
	const controller = new AbortController();
	const timeoutId = setTimeout(
		() => controller.abort(),
		SSO_REFRESH_TIMEOUT_MS,
	);

	try {
		const res = await fetch(endpoint, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${currentSso}`,
				...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
			},
			signal: controller.signal,
		});

		if (!res.ok) {
			throw new Error(`SSO refresh failed: ${res.status}`);
		}
		const data = await res.json();
		const token: string = data?.token;
		const expiresAt: string | undefined = data?.expiresAt;
		if (!token) throw new Error("SSO refresh: resposta sem token");
		setShortSsoToken(token, expiresAt);
		return { token, expiresAt };
	} catch (e: any) {
		if (e.name === "AbortError") {
			throw new Error(`SSO refresh timeout após ${SSO_REFRESH_TIMEOUT_MS}ms`);
		}
		throw e;
	} finally {
		clearTimeout(timeoutId);
	}
}

export async function issueSsoTokenWithBaseBearer(
	getBaseBearer?: () => Promise<string>,
) {
	if (!getBaseBearer) throw new Error("Base bearer provider ausente");
	const base = await getBaseBearer();
	const tenantId = getTenantId();
	// Usar API_URL do config
	const res = await fetch(`${API_URL}/auth/cdr/sso-token`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${base}`,
			...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
		},
	});
	if (!res.ok) {
		const msg = `SSO issue failed: ${res.status}`;
		throw new Error(msg);
	}
	const data = await res.json();
	const token: string = data?.token;
	const expiresAt: string | undefined = data?.expiresAt;
	if (!token) throw new Error("SSO issue: resposta sem token");
	setShortSsoToken(token, expiresAt);
	return { token, expiresAt };
}

/**
 * Fallback para renovação de token quando getBaseBearer não está disponível.
 * Tenta múltiplas estratégias de renovação.
 */
export async function issueSsoTokenFallback(): Promise<{
	token: string;
	expiresAt?: string;
}> {
	try {
		// Estratégia 1: Tentar refresh independente com o próprio SSO
		try {
			const refreshed = await refreshSsoToken();
			return refreshed;
		} catch (e) {
			// Continuar para próxima estratégia
		}

		// Estratégia 2: Tentar emitir usando o token base do Docker 1 (se disponível)
		const baseBearer = await (async () => {
			try {
				if (typeof (window as any).getBaseBearer === "function") {
					return await (window as any).getBaseBearer();
				}
			} catch {}
			return null;
		})();
		if (baseBearer) {
			const endpoint = `${API_URL}/auth/cdr/sso-token`;
			const tenantId = getTenantId();
			const res = await fetch(endpoint, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${baseBearer}`,
					...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
				},
			});
			if (res.ok) {
				const data = await res.json();
				const token: string = data?.token;
				const expiresAt: string | undefined = data?.expiresAt;
				if (token) {
					setShortSsoToken(token, expiresAt);
					return { token, expiresAt };
				}
			}
		}

		// Estratégia 3: Tentar renovar sessão do app (fluxo legacy)
		try {
			const refreshed = await refreshToken();
			if (refreshed) {
				// Após renovar sessão, tentar novamente refresh SSO
				const refreshedSso = await refreshSsoToken().catch(() => null);
				if (refreshedSso) return refreshedSso as any;
			}
		} catch (refreshError) {
			// Continuar para próxima estratégia
		}

		// Estratégia 4: Tentar usar o token SSO atual se ainda válido
		const existingSsoToken = getShortSsoToken();
		if (existingSsoToken) {
			const secs = secondsToExpiry();
			if (secs && secs > 0) {
				// Token ainda válido, usar ele mesmo
				return { token: existingSsoToken };
			}
		}

		// REMOVIDO: Estratégia 5 (usar token expirado)
		// MOTIVO: Comportamento imprevisível e mascaramento de erros de autenticação
		// EM VEZ DISSO: Falhar explicitamente para forçar re-autenticação

		sendToTerminal(
			"❌ [SSO_TOOLS] Todas as estratégias de renovação falharam",
			{
				attemptedStrategies: [
					"refresh",
					"baseBearer",
					"legacyRefresh",
					"existingValid",
				],
				recommendation: "Usuário deve fazer login novamente",
			},
		);

		throw new Error(
			"SSO token inválido. Todas as tentativas de renovação falharam. Por favor, faça login novamente.",
		);
	} catch (e) {
		throw e;
	}
}

/**
 * Implementa getBaseBearer() conforme documentação da API
 * Retorna o token JWT válido do sistema para renovação de SSO
 */
export function setBaseBearer(token: string) {
	TokenManager.setBaseBearer(token);
	try {
		(window as any).toolsSso = (window as any).toolsSso || {};
		(window as any).toolsSso.setBaseBearer = setBaseBearer;
		(window as any).toolsSso.getBaseBearer = getBaseBearer();
	} catch {}
}

export function getBaseBearer(): () => Promise<string> {
	return async () => {
		// 1) Se houver função global (fornecida pelo Docker 1), usar
		try {
			if (typeof (window as any).getBaseBearer === "function") {
				const t = await (window as any).getBaseBearer();
				if (t) return t;
			}
		} catch {}

		// 2) Tentar base bearer específico do TokenManager
		const baseBearerToken = TokenManager.getBaseBearer();
		if (baseBearerToken) {
			return baseBearerToken;
		}

		// 3) Fallback: accessToken do TokenManager
		const accessToken = TokenManager.getAccessToken();
		if (accessToken) {
			return accessToken;
		}

		throw new Error("Base bearer ausente");
	};
}

/**
 * Troca um JWT principal (aud: "cdr") por um SSO token real (aud: "tools_sso")
 * via POST /auth/cdr/sso-token. Isso garante que o auto-refresh funcione,
 * pois o endpoint /auth/cdr/sso-token/refresh exige aud: "tools_sso".
 *
 * @param accessToken JWT principal recebido do Launcher/addon
 * @returns true se a troca foi bem-sucedida; false caso contrário
 */
export async function exchangeForSsoToken(
	accessToken: string,
): Promise<boolean> {
	try {
		const tenantId = getTenantId();
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), 5000);

		const res = await fetch(`${API_URL}/auth/cdr/sso-token`, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
			},
			signal: controller.signal,
		});
		clearTimeout(timeoutId);

		if (!res.ok) return false;

		const data = await res.json();
		if (!data?.token) return false;

		setShortSsoToken(data.token, data.expiresAt);
		sendToTerminal("✅ [SSO] Token SSO real emitido via exchange", {
			hasExpiresAt: !!data.expiresAt,
		});
		return true;
	} catch {
		return false;
	}
}

/**
 * Fetch com retry único em 401 para rotas que usam aud: "tools_sso"
 * Implementa a estratégia oficial de renovação
 *
 * ✅ OTIMIZADO: Verifica token apenas se realmente necessário (< 5 minutos para expirar)
 * para evitar verificações desnecessárias em cada requisição
 */
export async function fetchWithSsoRetry(
	url: string,
	options: RequestInit = {},
	getBaseBearer?: () => Promise<string>,
) {
	// ✅ OTIMIZADO: Verificar apenas se token expira em < 5 minutos
	const secs = secondsToExpiry();
	if (secs != null && secs <= RENEWAL_THRESHOLD_SEC) {
		await ensureFreshSsoToken(getBaseBearer);
	}

	// Helper para construir headers com Authorization, X-Session-Token e X-Tenant-Id
	const buildHeaders = (): HeadersInit => {
		const sessionToken = (window as any).sessionToken || null;
		const ssoToken = getShortSsoToken();
		const tenantId = getTenantId();
		const headers: Record<string, string> = {
			...((options.headers as Record<string, string>) || {}),
			Authorization: `Bearer ${ssoToken}`,
			...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
		};

		// Adicionar X-Session-Token se disponível (C# valida este header)
		if (sessionToken) {
			headers["X-Session-Token"] = sessionToken;
		}

		return headers;
	};

	// 2. Primeira tentativa
	let res: Response;
	try {
		res = await fetch(url, {
			...options,
			headers: buildHeaders(),
		});
	} catch (fetchError: any) {
		// Erro de fetch (Failed to fetch, CORS, etc) - re-throw para o componente tratar
		throw fetchError;
	}

	// 3. Retry único em 401
	if (res.status === 401) {
		try {
			// 3.1 Tentar refresh próprio primeiro
			try {
				await refreshSsoToken();
			} catch (e) {
				// 3.2 Se refresh falhar, tentar emissão com base bearer (se disponível)
				if (getBaseBearer) {
					await issueSsoTokenWithBaseBearer(getBaseBearer);
				} else {
					// 3.3 Fallback multi-estratégia (tenta refresh novamente e emissão)
					await issueSsoTokenFallback();
				}
			}
			res = await fetch(url, {
				...options,
				headers: buildHeaders(),
			});
		} catch (e) {
			// Retorna 401 original se retry falhar
		}
	}

	return res;
}

/**
 * Garante que o token SSO está válido, renovando se necessário.
 *
 * ✅ OTIMIZADO:
 * - Threshold aumentado de 30s para 300s (5 minutos antes de expirar)
 * - Lock implementado para evitar renovações simultâneas
 * - Cooldown de 5 segundos entre renovações
 * - Reutiliza renovação em andamento se múltiplas requisições chegarem simultaneamente
 *
 * @param getBaseBearer Função opcional para obter token base para renovação
 */
export async function ensureFreshSsoToken(
	getBaseBearer?: () => Promise<string>,
) {
	const secs = secondsToExpiry();
	if (secs == null) return; // sem exp salvo, deixa passar

	// ✅ OTIMIZADO: Renovar apenas se faltam menos de 5 minutos (vs 30s antes)
	// Considerando que o token tem 1 hora de duração, renovar 5 min antes é suficiente
	if (secs > RENEWAL_THRESHOLD_SEC) return; // ainda válido por mais de 5 minutos

	// ✅ NOVO: Cooldown para evitar renovações muito frequentes
	const now = Date.now();
	if (now - lastRefreshTime < REFRESH_COOLDOWN_MS) {
		// Renovação recente, usar token atual
		return;
	}

	// ✅ NOVO: Lock - reutilizar renovação em andamento
	if (refreshInProgress) {
		// Já existe uma renovação em andamento, aguardar ela
		try {
			await refreshInProgress;
			return;
		} catch {
			// Se falhar, continuar para tentar renovar
			refreshInProgress = null;
		}
	}

	// Criar promise de renovação
	refreshInProgress = (async () => {
		try {
			// 1) Priorizar refresh independente
			try {
				const result = await refreshSsoToken();
				lastRefreshTime = Date.now();
				return result;
			} catch (e) {
				// Continuar para próxima estratégia
			}

			// 2) Tentar emissão com base bearer (se fornecido)
			if (getBaseBearer) {
				const result = await issueSsoTokenWithBaseBearer(getBaseBearer);
				lastRefreshTime = Date.now();
				return result;
			}

			// 3) Fallback multi-estratégia (refresh + emissão)
			const result = await issueSsoTokenFallback();
			lastRefreshTime = Date.now();
			return result;
		} finally {
			refreshInProgress = null;
		}
	})();

	try {
		await refreshInProgress;
	} catch (e) {
		// Se todas as estratégias falharam, tentar usar o token atual se ainda válido
		const existingToken = getShortSsoToken();
		if (existingToken && secs && secs > 0) {
			// Token ainda válido, continuar com ele
			return;
		}

		// ⚠️ REMOVIDO: Fallback perigoso que usava accessToken como ssoToken
		// accessToken (aud: "cdr") != ssoToken (aud: "tools_sso")
		// Usar accessToken como ssoToken causava 401 silenciosos

		// Disparar evento para notificar UI sobre falha de autenticação
		try {
			window.dispatchEvent(
				new CustomEvent("sso:expired", {
					detail: {
						reason: "Todas as tentativas de renovação do SSO falharam",
					},
				}),
			);
		} catch {}

		sendToTerminal(
			"❌ [SSO] Token expirado e renovação falhou - usuário precisa reabrir ferramentas",
			{
				hadExistingToken: !!existingToken,
				secondsRemaining: secs,
			},
		);
	}
}
